import { DayData, RetentionDayData } from "../../types";

export const RANGES = [7, 30, 60, 90] as const;
export type RangeDays = (typeof RANGES)[number];

/** One day of a metric, oldest first once it has been through `toPoints`. */
export type Point = { day: string; label: string; value: number };

export type Periods = { current: Point[]; previous: Point[] | null };

export const RETENTION_DAYS = 12;

/** A retention cell: the percent of the cohort that came back, or null before that day has happened. */
export type RetentionCell = number | null;

export type RetentionRow = {
  dateKey: string;
  signUpCount: number;
  cells: RetentionCell[];
};

/**
 * Normalises the API's day rows. When every `day` parses as a date the rows are sorted oldest
 * first, so the split into periods does not depend on the order the API happens to send.
 */
export function toPoints(rows: DayData[] | undefined): Point[] {
  const points = (rows ?? []).map((r) => ({
    day: r.day,
    label: r.dayDisplayText,
    value: Number(r.count) || 0,
  }));
  const times = points.map((p) => Date.parse(p.day));
  if (times.every((t) => !Number.isNaN(t))) {
    return points
      .map((p, i) => ({ p, t: times[i] }))
      .sort((a, b) => a.t - b.t)
      .map(({ p }) => p);
  }
  return points;
}

/**
 * The newest `range` days are the current period. The `range` days before them are the
 * previous period, but only when the API returned all of them.
 */
export function splitPeriods(points: Point[], range: number): Periods {
  const current = points.slice(-range);
  const previous =
    points.length >= range * 2 ? points.slice(-range * 2, -range) : null;
  return { current, previous };
}

export const values = (points: Point[]) => points.map((p) => p.value);

export const sum = (xs: number[]) => xs.reduce((s, x) => s + x, 0);

export const mean = (xs: number[]) => (xs.length ? sum(xs) / xs.length : 0);

/** Percent change from `previous` to `current`; null when there is nothing to compare against. */
export function percentChange(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

export const formatCount = (n: number) => numberFormat.format(Math.round(n));

export const formatDecimal = (n: number, digits: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);

export type Direction = "up" | "down" | "flat";

/** Changes that round to 0.0 are flat, so they are not coloured as a rise. */
export const changeDirection = (change: number): Direction =>
  Math.abs(change) < 0.05 ? "flat" : change > 0 ? "up" : "down";

/** "▲ 4.2%" or "▼ 1.3 pts"; the arrow carries direction so colour is not the only signal. */
export function formatChange(change: number, unit: "%" | " pts") {
  const direction = changeDirection(change);
  if (direction === "flat") return "No change";
  const arrow = direction === "up" ? "▲" : "▼";
  return `${arrow} ${formatDecimal(Math.abs(change), 1)}${unit}`;
}

const dayCount = (row: RetentionDayData, day: number) =>
  Number(row[`day${day}Count` as keyof RetentionDayData]) || 0;

/** A cohort's Day N figure only exists once N days have passed since it signed up. */
const reached = (row: RetentionDayData, day: number) =>
  row.signUpCount > 0 && day <= row.daysAgo;

export function retentionRows(rows: RetentionDayData[]): RetentionRow[] {
  return rows.map((row) => ({
    dateKey: row.dateKey,
    signUpCount: row.signUpCount,
    cells: Array.from({ length: RETENTION_DAYS }, (_, i) => {
      const day = i + 1;
      return reached(row, day)
        ? Math.round((dayCount(row, day) / row.signUpCount) * 100)
        : null;
    }),
  }));
}

/** Day N retention across every cohort that has reached Day N, weighted by cohort size. */
export function retentionAverages(rows: RetentionDayData[]): RetentionCell[] {
  return Array.from({ length: RETENTION_DAYS }, (_, i) => {
    const day = i + 1;
    const cohorts = rows.filter((row) => reached(row, day));
    const signUps = sum(cohorts.map((row) => row.signUpCount));
    if (signUps === 0) return null;
    return Math.round((sum(cohorts.map((row) => dayCount(row, day))) / signUps) * 100);
  });
}

const csvField = (v: string | number) => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function toCsv(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows].map((r) => r.map(csvField).join(",")).join("\n");
}

/**
 * Lines several series up by day, oldest first. A day missing from one series is null rather
 * than zero, so a chart leaves a gap and an export leaves the cell blank.
 */
export function mergeByDay(
  series: Point[][]
): { day: string; label: string; values: (number | null)[] }[] {
  const days = new Map<string, string>();
  series.forEach((points) => points.forEach((p) => days.set(p.day, p.label)));
  const lookups = series.map((points) => new Map(points.map((p) => [p.day, p.value])));
  return toPoints(
    Array.from(days, ([day, label], key) => ({ key, day, dayDisplayText: label, count: 0 }))
  ).map((p) => ({
    day: p.day,
    label: p.label,
    values: lookups.map((m) => m.get(p.day) ?? null),
  }));
}
